/**
 * Keeps Athos recommendations filled in the cart drawer.
 *
 * Athos fills its `.ss__recs__*` targets once, on page load. The drawer swaps its content when the cart
 * changes (the empty state is cloned from #empty-cart-template, the populated state is morphed in from the
 * Section Rendering API), and the new target elements arrive empty. This watches the drawer and asks Athos
 * to retarget whenever an empty target appears.
 *
 * Targets:
 * - `.ss__recs__empty-cart`  empty cart (Athos Recs Script block with selector "empty-cart")
 * - `.ss__recs__cart-drawer` cart with items. Without its own Athos profile (a block with selector
 *   "cart-drawer"), it falls back to the empty cart profile.
 */
(function () {
  const EMPTY_CLASS = 'ss__recs__empty-cart';
  const POPULATED_CLASS = 'ss__recs__cart-drawer';
  const SELECTORS = [`.${EMPTY_CLASS}`, `.${POPULATED_CLASS}`];

  function recommendationControllers() {
    const controllers = window.athos && window.athos.controller;
    if (!controllers) return [];
    return Object.values(controllers).filter(
      (controller) => controller && controller.type === 'recommendation' && controller.targeters
    );
  }

  /** @param {HTMLElement} dialog */
  function refresh(dialog) {
    const controllers = recommendationControllers();
    if (!controllers.length) return;

    const populated = dialog.querySelector(`.${POPULATED_CLASS}`);
    const hasPopulatedProfile = controllers.some((controller) => controller.targeters[`.${POPULATED_CLASS}`]);
    if (populated && !hasPopulatedProfile) populated.classList.add(EMPTY_CLASS);

    const targets = dialog.querySelectorAll(SELECTORS.join(', '));
    const needsFill = Array.from(targets).some((target) => target.childElementCount === 0);
    if (!needsFill) return;

    controllers.forEach((controller) => {
      SELECTORS.forEach((selector) => {
        const targeter = controller.targeters[selector];
        if (targeter && typeof targeter.retarget === 'function') targeter.retarget();
      });
    });
  }

  /** @param {() => void} callback */
  function whenAthosReady(callback) {
    if (recommendationControllers().length) return callback();
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (recommendationControllers().length) {
        clearInterval(timer);
        callback();
      } else if (attempts >= 60) {
        clearInterval(timer);
      }
    }, 250);
  }

  function init() {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLElement)) return;

    whenAthosReady(() => {
      refresh(dialog);

      /** @type {number | undefined} */
      let timeout;
      const observer = new MutationObserver(() => {
        clearTimeout(timeout);
        timeout = setTimeout(() => refresh(dialog), 100);
      });
      observer.observe(dialog, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
